import { useTranslation } from "react-i18next"
import {
  Button,
  Icon,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui"
import {
  PERSONA_FILTER_ALL,
  PersonaFilters,
  type PersonaFiltersProps,
  type PersonaStatusFilter,
} from "../PersonaFilters/PersonaFilters"

export type PersonaLibraryViewMode = "table" | "cards"

export interface PersonaLibraryToolbarProps extends PersonaFiltersProps {
  viewMode: PersonaLibraryViewMode
  onViewModeChange: (mode: PersonaLibraryViewMode) => void
  includeRejected: boolean
  onIncludeRejectedChange: (value: boolean) => void
  /** Standing review / finish entry when the banner is dismissed. */
  reviewAction?: {
    kind: "review" | "finish"
    onClick: () => void
    disabled?: boolean
    disabledReason?: string
  }
}

const VIEW_OPTIONS: { value: PersonaLibraryViewMode; icon: "rows-3" | "layout-grid" }[] = [
  { value: "table", icon: "rows-3" },
  { value: "cards", icon: "layout-grid" },
]

export function PersonaLibraryToolbar({
  viewMode,
  onViewModeChange,
  includeRejected,
  onIncludeRejectedChange,
  reviewAction,
  ...filterProps
}: PersonaLibraryToolbarProps) {
  const { t } = useTranslation()
  const showRejectedLabel = t("personas.toolbar.showRejected", "Show rejected")
  const tableViewLabel = t("personas.toolbar.viewTable", "Table view")
  const cardsViewLabel = t("personas.toolbar.viewCards", "Cards view")

  return (
    <div
      className="flex flex-nowrap items-center gap-2 overflow-x-auto"
      data-testid="persona-library-toolbar"
    >
      <PersonaFilters {...filterProps} />

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <div
          className="relative inline-flex items-center rounded-md border border-border/60 p-0.5"
          role="group"
          aria-label={t("personas.toolbar.viewToggleAria", "Library view")}
        >
          {/* Thumb travels one button width (`size-8` = `translate-x-8`) per option. */}
          <span
            aria-hidden
            className={cn(
              "pointer-events-none absolute left-0.5 top-0.5 size-8 rounded-sm bg-primary/10",
              "transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
              viewMode === "cards" ? "translate-x-8" : "translate-x-0",
            )}
          />
          {VIEW_OPTIONS.map((opt) => {
            const active = viewMode === opt.value
            const label = opt.value === "table" ? tableViewLabel : cardsViewLabel
            return (
              <Tooltip key={opt.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-pressed={active}
                    aria-label={label}
                    onClick={() => onViewModeChange(opt.value)}
                    className={cn(
                      "relative inline-flex size-8 items-center justify-center rounded-sm transition-colors duration-150 ease-out",
                      active ? "text-primary" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon name={opt.icon} size="xs" color="inherit" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{label}</TooltipContent>
              </Tooltip>
            )
          })}
        </div>

        <label className="inline-flex h-8 cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={includeRejected}
            onCheckedChange={onIncludeRejectedChange}
            aria-label={showRejectedLabel}
          />
          <span className="whitespace-nowrap">{showRejectedLabel}</span>
        </label>

        {reviewAction ? (
          reviewAction.disabled && reviewAction.disabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    size="sm"
                    variant={reviewAction.kind === "finish" ? "default" : "outline"}
                    disabled
                  >
                    {reviewAction.kind === "finish"
                      ? t("personas.toolbar.finishSetup", "Finish setup")
                      : t("personas.toolbar.review", "Review")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{reviewAction.disabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              type="button"
              size="sm"
              variant={reviewAction.kind === "finish" ? "default" : "outline"}
              onClick={reviewAction.onClick}
              disabled={reviewAction.disabled}
            >
              {reviewAction.kind === "finish"
                ? t("personas.toolbar.finishSetup", "Finish setup")
                : t("personas.toolbar.review", "Review")}
            </Button>
          )
        ) : null}
      </div>
    </div>
  )
}

export type { PersonaStatusFilter }
export { PERSONA_FILTER_ALL }
